import type { Middleware } from '@reduxjs/toolkit';
import { isAnyOf } from '@reduxjs/toolkit';
import { keys } from 'lodash-es';
import { MEMOIZE_BLOCK_VERSION } from 'src/services/memoize/cache';
import { observableStoreOp } from 'src/services/rxjs-redux/map';
import {
  getSelectedBlocksKey,
  getSelectedCellKey,
  prefixSelectBlock,
  prefixSelectCell,
  prefixSimpleTable,
  resetSelectValue,
} from 'src/services/rxjs-redux/patch';
import { spaceViewsOperation } from 'src/services/spaces/space-views';
import { spacesOperation } from 'src/services/spaces/spaces';
import {
  ADD_MARK_NAME,
  CREATE_BLOCK,
  CREATE_COLLECTION_VIEW,
  CREATE_SPACE,
  CREATE_SPACE_VIEW,
  DELETE_SPACE_VIEW,
  DESTROY_BLOCK,
  LIST_AFTER_BLOCK,
  LIST_AFTER_COLLECTION_VIEW,
  LIST_AFTER_FAVORITE,
  LIST_AFTER_TEMPLATE,
  LIST_BEFORE_BLOCK,
  LIST_BEFORE_COLLECTION_VIEW,
  LIST_BEFORE_FAVORITE,
  LIST_BEFORE_TEMPLATE,
  LIST_REMOVE_BLOCK,
  LIST_REMOVE_COLLECTION_VIEW,
  LIST_REMOVE_COLLECTION_VIEW_PAGESORT,
  LIST_REMOVE_FAVORITE,
  LIST_REMOVE_TEMPLATE,
  UPDATE_COLLECTION_VIEW,
  UPDATE_SPACE,
  UPDATE_SPACE_VIEW,
} from '../actions';
import { CREATE_COMMENT } from '../actions/comments';
import { CREATE_DISCUSSION } from '../actions/discussion';
import { blocksActions } from '../reducers/blocks';
import { collectionViewsActions } from '../reducers/collection-views';
import { commentsActions } from '../reducers/comments';
import { discussionsActions } from '../reducers/discussions';
import { simpleTableActions } from '../reducers/simple-table';
import { uiActions } from '../reducers/ui';
import type { RootState, SelectBlock, SelectCell } from '../types';

export const patchRxjsMiddleware: Middleware = ({ getState }: { getState: () => RootState }) => {
  /** 选中 block 推送 rx 订阅 */
  const nextSelectBlock = (item: SelectBlock) => {
    if (item.syncId) {
      const key = getSelectedBlocksKey({ syncId: item.syncId });
      observableStoreOp.patch(key);
    }

    if (item.blockId) {
      const key = getSelectedBlocksKey({ blockId: item.blockId });
      observableStoreOp.patch(key);
    }

    if (item.viewId) {
      const key = getSelectedBlocksKey({ viewId: item.viewId });
      observableStoreOp.patch(key);
    }

    if (item.blockId && item.syncId && item.viewId) {
      const key = getSelectedBlocksKey(item);
      observableStoreOp.patch(key);
    }
  };

  const nextSelectCell = (item: SelectCell) => {
    if (item.recordId) {
      const key = getSelectedCellKey({ recordId: item.recordId });
      observableStoreOp.patch(key);
    }

    if (item.viewId) {
      const key = getSelectedCellKey({ viewId: item.viewId });
      observableStoreOp.patch(key);
    }

    if (item.recordId) {
      const key = getSelectedCellKey({ recordId: item.recordId });
      observableStoreOp.patch(key);
    }

    if (item.recordId && item.viewId && item.propertyId) {
      const key = getSelectedCellKey(item);
      observableStoreOp.patch(key);
    }
  };

  const cleanPrefixData = (prefix: string) => {
    const opMap = observableStoreOp.getMap();
    opMap.forEach((item, key) => {
      if (key?.startsWith(prefix)) {
        const value = item.getValue();
        if (value && item.observed) {
          observableStoreOp.patch(key, resetSelectValue);
        }
      }
    });
  };

  /** 清空所有选中的数据 */
  const updateAllPrefixSelect = () => {
    cleanPrefixData(prefixSelectBlock);
    cleanPrefixData(prefixSelectCell);
  };

  return (next) => (action) => {
    const runPatch = () => {
      // 特殊优化
      if (
        isAnyOf(blocksActions.incVersion, UPDATE_COLLECTION_VIEW, CREATE_COLLECTION_VIEW)(action)
      ) {
        MEMOIZE_BLOCK_VERSION.clear();
      }

      if (isAnyOf(blocksActions.clear, discussionsActions.clear)(action)) {
        observableStoreOp.clear();
        return;
      }
      // #region 处理同样的参数

      /**
       * 包含了
       * UPDATE_COLLECTION_VIEW
       * LIST_REMOVE_COLLECTION_VIEW_PAGESORT
       * LIST_REMOVE_BLOCK
       * LIST_AFTER_BLOCK
       * LIST_BEFORE_BLOCK
       * LIST_AFTER_COLLECTION_VIEW
       * LIST_BEFORE_COLLECTION_VIEW
       * LIST_REMOVE_COLLECTION_VIEW
       * LIST_REMOVE_COLLECTION_VIEW_PAGESORT
       * UPDATE_BLOCK
       * SET_BLOCK_PERMISSION
       * REMOVE_BLOCK_PERMISSION
       * UPDATE_DISCUSSION
       * LIST_REMOVE_DISCUSSION
       * LIST_AFTER_DISCUSSION
       * UPDATE_COMMENT
       * LIST_AFTER_COMMENT
       * LIST_REMOVE_COMMENT
       * LIST_BEFORE_COLLECTION_VIEW_PAGESORT
       * LIST_AFTER_COLLECTION_VIEW_PAGESORT
       */
      if (action.payload?.uuid) {
        observableStoreOp.patch(action.payload.uuid);
      }

      if (action.payload?.viewId) {
        observableStoreOp.patch(action.payload.viewId);
      }

      if (action.payload?.parentId) {
        observableStoreOp.patch(action.payload.parentId);
      }

      if (action.payload?.after) {
        observableStoreOp.patch(action.payload.after);
      }

      if (action.payload?.before) {
        observableStoreOp.patch(action.payload.before);
      }

      // #endregion

      // #region collectionViews
      if (isAnyOf(collectionViewsActions.update)(action)) {
        keys(action.payload).forEach((key) => {
          observableStoreOp.patch(key);
        });
        return;
      }

      if (isAnyOf(CREATE_COLLECTION_VIEW)(action)) {
        observableStoreOp.patch(action.payload.collectionView.uuid);
        return;
      }
      // #endregion

      // #region simpleTable
      if (isAnyOf(simpleTableActions.update)(action)) {
        observableStoreOp.patch(prefixSimpleTable);
        return;
      }

      // #endregion

      // #region block
      if (
        isAnyOf(
          LIST_REMOVE_BLOCK,
          LIST_AFTER_BLOCK,
          LIST_BEFORE_BLOCK,
          LIST_AFTER_COLLECTION_VIEW,
          LIST_BEFORE_COLLECTION_VIEW,
          LIST_REMOVE_COLLECTION_VIEW,
          LIST_REMOVE_COLLECTION_VIEW_PAGESORT
        )(action)
      ) {
        // observableStoreOp.patch(action.payload.uuid);
        const block = getState().blocks[action.payload.uuid];
        if (block?.parentId) {
          observableStoreOp.patch(block.parentId);
        }
        return;
      }

      if (isAnyOf(CREATE_BLOCK)(action)) {
        const { block } = action.payload;
        observableStoreOp.patch(block.uuid);
        return;
      }

      if (isAnyOf(DESTROY_BLOCK)(action)) {
        const { uuid } = action.payload;
        observableStoreOp.deleteObs(uuid);
        return;
      }

      if (isAnyOf(blocksActions.update)(action)) {
        keys(action.payload.blocks).forEach((key) => {
          observableStoreOp.patch(key);
        });
        return;
      }

      if (isAnyOf(blocksActions.incVersion)(action)) {
        const ids = action.payload;
        ids.forEach((id) => {
          const block = getState().blocks[id];
          if (block) {
            observableStoreOp.patch(id);
          }
        });
        return;
      }

      // #endregion

      // #region spaces
      if (isAnyOf(CREATE_SPACE)(action)) {
        spacesOperation.create(action.payload.space);
        return;
      }

      if (isAnyOf(UPDATE_SPACE)(action)) {
        spacesOperation.update(action.payload);
        return;
      }

      if (isAnyOf(ADD_MARK_NAME)(action)) {
        spacesOperation.addMarkName(action.payload);
        return;
      }

      if (isAnyOf(LIST_REMOVE_TEMPLATE)(action)) {
        spacesOperation.listRemoveTemplate(action.payload);
        return;
      }

      if (isAnyOf(LIST_BEFORE_TEMPLATE)(action)) {
        spacesOperation.listBeforeTemplate(action.payload);
        return;
      }

      if (isAnyOf(LIST_AFTER_TEMPLATE)(action)) {
        spacesOperation.listAfterTemplate(action.payload);
        return;
      }
      // #endregion

      // #region spaceViews
      if (isAnyOf(CREATE_SPACE_VIEW)(action)) {
        spaceViewsOperation.create(action.payload);
        return;
      }

      if (isAnyOf(DELETE_SPACE_VIEW)(action)) {
        spaceViewsOperation.delete(action.payload);
        return;
      }

      if (isAnyOf(UPDATE_SPACE_VIEW)(action)) {
        spaceViewsOperation.updateSpaceView(action.payload);
        return;
      }

      if (isAnyOf(LIST_REMOVE_FAVORITE)(action)) {
        spaceViewsOperation.listRemoveFavorite(action.payload);
        return;
      }

      if (isAnyOf(LIST_BEFORE_FAVORITE)(action)) {
        spaceViewsOperation.listBeforeFavorite(action.payload);
        return;
      }

      if (isAnyOf(LIST_AFTER_FAVORITE)(action)) {
        spaceViewsOperation.listAfterFavorite(action.payload);
        return;
      }

      // #endregion

      // #region discussions
      if (isAnyOf(discussionsActions.update)(action)) {
        keys(action.payload).forEach((key) => {
          observableStoreOp.patch(key);
        });
        return;
      }

      if (isAnyOf(CREATE_DISCUSSION)(action)) {
        observableStoreOp.patch(action.payload.discussion.uuid);
        return;
      }

      // if (isAnyOf(UPDATE_DISCUSSION)(action)) {
      //   observableStoreOp.patch(action.payload.uuid);
      //   return;
      // }

      // if (isAnyOf(LIST_REMOVE_DISCUSSION)(action)) {
      //   observableStoreOp.patch(action.payload.uuid);
      //   observableStoreOp.patch(action.payload.parentId);
      //   return;
      // }

      // if (isAnyOf(LIST_AFTER_DISCUSSION)(action)) {
      //   observableStoreOp.patch(action.payload.uuid);
      //   observableStoreOp.patch(action.payload.parentId);
      //   action.payload.after && observableStoreOp.patch(action.payload.after);
      //   return;
      // }
      // #endregion

      // #region comments
      if (isAnyOf(commentsActions.update)(action)) {
        keys(action.payload).forEach((key) => {
          observableStoreOp.patch(key);
        });
        return;
      }

      if (isAnyOf(CREATE_COMMENT)(action)) {
        observableStoreOp.patch(action.payload.comment.uuid);
        return;
      }

      // if (isAnyOf(UPDATE_COMMENT)(action)) {
      //   observableStoreOp.patch(action.payload.uuid);
      //   return;
      // }

      // if (isAnyOf(LIST_AFTER_COMMENT, LIST_REMOVE_COMMENT)(action)) {
      //   observableStoreOp.patch(action.payload.uuid);
      //   observableStoreOp.patch(action.payload.parentId);
      // }
      // #endregion

      // #region ui

      // 更新 blocks 和  cell
      if (isAnyOf(uiActions.update)(action)) {
        if (
          action.payload.selectedBlocks?.length === 0 &&
          action.payload.selectedCells?.length === 0
        ) {
          updateAllPrefixSelect();
          return;
        }

        if (action.payload.selectedBlocks) {
          cleanPrefixData(prefixSelectBlock);
          action.payload.selectedBlocks.forEach(nextSelectBlock);
        }

        if (action.payload.selectedCells) {
          cleanPrefixData(prefixSelectCell);
          action.payload.selectedCells.forEach(nextSelectCell);
        }
      }

      if (isAnyOf(uiActions.updateSelectBlocks)(action)) {
        if (action.payload.length) {
          cleanPrefixData(prefixSelectCell);
        }
        cleanPrefixData(prefixSelectBlock);
        action.payload.forEach(nextSelectBlock);
      }

      // 操作单条数据
      if (isAnyOf(uiActions.addSelectBlock, uiActions.removeSelectBlock)(action)) {
        nextSelectBlock(action.payload);
      }

      // 更新 cell
      if (isAnyOf(uiActions.updateSelectCells)(action)) {
        if (action.payload.length) {
          cleanPrefixData(prefixSelectBlock);
        }
        cleanPrefixData(prefixSelectCell);
        action.payload.forEach(nextSelectCell);
      }

      // #endregion
    };

    next(action);
    runPatch();
  };
};
